import { change, formValueSelector } from 'redux-form';
import { connect } from 'react-redux';
import { flow } from 'fp-ts/function';
import { useCurrentEnterprise } from '@mews-commander/legacy-entities/src/enterprise';
import {
    IdentityDocumentType as GeneratedIdentityDocumentType,
    IdentityDocumentPhoto,
} from '@mews-commander/data-access';
import { Optional } from '@mews/std';
import { ApplicationState, CommanderThunkDispatch, Option, WithScope } from '@mews-commander/types';
import { useFeatureFlag, lt } from '@mews/framework';
import { CountryCode, FeatureFlag, TextKey } from '@mews-commander/enums';
import { IdentityDocumentType } from '@mews-commander/legacy-data-structures';
import {
    commanderForm,
    DateStringPickerField,
    EnumifySelectField,
    dateString,
    InputField,
    maxInputTextLength,
    presence,
    SelectField,
    validation,
    withAutoFocusingFirstFocusableElement,
    CheckboxField,
} from '@mews-commander/legacy-form';
import { getCountryOptions } from '@mews-commander/legacy-entities/src/ducks/countries';
import { FieldsContainer } from '@mews-commander/ui';
import { Stack } from '@optimus-web/core';
import { getSelectedIdentityDocumentFormValues, getSelectedIdentityDocumentScans } from '../selectors';
import { CommanderSidebar } from '../../../components/sidebar';
import { IdentityDocumentScanPreview } from '../../../accounts/modules/identityDocumentScan/components/IdentityDocumentScan';
import { editIdentityDocument, IdentityDocumentFormValues } from '../actions';

interface InitialFormValues {
    issuingCountryCode: string | null;
    issuingCity: string | null;
    number: string | null;
    type: GeneratedIdentityDocumentType | null;
    issueDateString: Optional<string>;
    expiryDateString: Optional<string>;
    documentSupportNumber: string | null;
}

interface DispatchProps {
    handleClearDocumentSupportNumber: () => void;
}

interface UpdateOptions {
    values: IdentityDocumentFormValues;
    identityDocumentId: string;
}

interface OwnProps extends WithScope {
    ownerId: string;
    form: string;
    id?: string;
    isScanner?: boolean;
    hasIDCardPreview?: boolean;
    handleUpdate: ({ values, identityDocumentId }: UpdateOptions) => void;
    scans: IdentityDocumentPhoto[] | null | undefined;
    optionalNumberEnabled?: boolean;
}

interface StateProps {
    initialValues: InitialFormValues | null;
    countryOptions: Option[];
    displaySupportNumber?: boolean;
}

interface Props extends OwnProps, StateProps, DispatchProps {
    scanImageUrl: string | null | undefined;
    onClose?: () => void;
    onSuccess?: () => void;
    onSubmitFail?: () => void;
}

export const IdentityDocumentFormFields = ({
    countryOptions,
    onSuccess,
    onSubmitFail,
    isScanner,
    handleClearDocumentSupportNumber,
    displaySupportNumber,
    scanImageUrl,
    hasIDCardPreview = false,
    initialValues,
    scans,
    optionalNumberEnabled,
    ...other
}: Props) => {
    const { id: identityDocumentId, ownerId, handleUpdate } = other;
    const isScanEnabled = useFeatureFlag(FeatureFlag.identityDocumentScanEnabled);
    const currentEnterprise = useCurrentEnterprise();
    const defaultScans = scans != null ? scans : [];
    // This is not ideal solution, we plan on moving to Gql soon so this is just solution that works for now
    const update = () => {
        if (initialValues != null && identityDocumentId != null) {
            if (initialValues.type != null) {
                // @ts-expect-error In this case we are in edit form, editing thus the initialValues are the previous values and not Initial
                handleUpdate({ values: initialValues, identityDocumentId });
            }
        }
    };

    return (
        <FieldsContainer {...other}>
            <InputField label={lt('Number')} name="number" required={!optionalNumberEnabled} autoComplete="off" />
            <Stack vertical spacing="100">
                <EnumifySelectField {...typeProps} />
                {isScanEnabled && (
                    <CheckboxField name="isVerified" label={lt(TextKey.IdentityDocumentVerificationCheckboxLabel)} />
                )}
            </Stack>
            <Stack vertical spacing="500">
                {isScanEnabled && hasIDCardPreview && defaultScans?.length > 0
                    ? defaultScans.map((scan) => (
                          <IdentityDocumentScanPreview
                              key={scan.id}
                              updateDocument={update}
                              ownerId={ownerId}
                              identityDocumentId={identityDocumentId}
                              scan={scan}
                          />
                      ))
                    : null}
                <SelectField
                    {...issuingCountryProps}
                    options={countryOptions}
                    onChange={handleClearDocumentSupportNumber}
                />
            </Stack>
            {displaySupportNumber &&
            currentEnterprise.legalEnvironmentCode.includes(CountryCode.Spain) &&
            !isScanner ? (
                <InputField {...documentSupportNumberProps} />
            ) : null}
            {!isScanner ? <InputField {...issuingCityProps} /> : null}
            {!isScanner ? <DateStringPickerField {...issueDateProps} /> : null}
            <DateStringPickerField {...expiryDateProps} />
        </FieldsContainer>
    );
};

const issuingCountryProps = {
    label: lt('IssuingCountry'),
    name: 'issuingCountryCode',
    filterable: true,
};

const issuingCityProps = {
    label: lt('IssuingCity'),
    name: 'issuingCity',
};

const typeProps = {
    enumClass: IdentityDocumentType,
    label: lt('Type'),
    name: 'type',
    required: true,
    useNameAsValue: true,
};

const issueDateProps = {
    name: 'issueDateString',
    label: lt('IssueDate'),
    showMonthSelector: true,
};

const expiryDateProps = {
    name: 'expiryDateString',
    label: lt('ExpirationDate'),
    showMonthSelector: true,
};

const documentSupportNumberProps = {
    name: 'documentSupportNumber',
    label: lt('IdentityDocumentSupportNumber'),
    required: true,
    autoComplete: 'off',
};

export const getConstraints = ({
    optionalNumberEnabled,
    displaySupportNumber,
}: {
    optionalNumberEnabled?: boolean;
    displaySupportNumber?: boolean;
}) => ({
    number: optionalNumberEnabled ? undefined : { presence },
    type: {
        presence,
    },
    documentSupportNumber: displaySupportNumber
        ? {
              presence,
              length: maxInputTextLength,
          }
        : undefined,
    issuingCity: {
        length: maxInputTextLength,
    },
    expiryDateString: {
        dateString,
    },
});

export default flow(
    commanderForm({
        enableReinitialize: true,
        submitLabel: lt('Save'),
        validate: validation(getConstraints),
        submitButtonProps: { variant: 'primary' },
    }),
    withAutoFocusingFirstFocusableElement(),
    connect<StateProps, DispatchProps, OwnProps, ApplicationState>(
        (state, { scope, form, ownerId }: OwnProps) => ({
            countryOptions: getCountryOptions(state),
            displaySupportNumber:
                formValueSelector(form)(state, 'issuingCountryCode') === CountryCode.Spain &&
                formValueSelector(form)(state, 'type') === GeneratedIdentityDocumentType.IdentityCard,
            initialValues: getSelectedIdentityDocumentFormValues(state, { scope, ownerId }),
            scans: getSelectedIdentityDocumentScans(state, { scope, ownerId }),
        }),
        (dispatch: CommanderThunkDispatch, { form, ownerId }) => ({
            handleClearDocumentSupportNumber: () => dispatch(change(form, 'documentSupportNumber', null)),
            handleUpdate: ({ values, identityDocumentId }: UpdateOptions) =>
                dispatch(editIdentityDocument({ ...values, ownerId, id: identityDocumentId })),
        })
    ),
    CommanderSidebar
)(IdentityDocumentFormFields);
